/**
 * The Reason line of an Activity decision, as the detail paints it — one builder, so every surface that repeats
 * the engine's reason (the Activity detail, Last changes' Ask Iris evidence, CU-869fn0gcp) says the same words.
 *
 * `row` is an Activity row after `useActivity`'s normalisation: `row.reason` is `reasonText(payload.reason)`,
 * `row.derivation.incomplete_market` the engine's block. The two thin-market exits and `no_strategy` are composed
 * by their own builders (`incompleteMarket`, `faceScope`); every other reason is the engine's sentence or the
 * words below for its code. No reason recorded → `null`: the caller decides what to say, never this module.
 *
 * Pure and alias-free (relative imports only): `node --test` runs it and Last changes imports it.
 */
import { incompleteMarketNoChange } from './incompleteMarket.js'
import { noStrategyCopy } from './faceScope.js'

//: The coded reasons, said out loud. Anything not here is already a
//: sentence and travels unchanged.
export const REASON_TEXT = {
	//: True whether the strategy's scope or the like-with-like filter emptied
	//: the list (CU-869fd5fnu): only offers of the same insurance and tariff
	//: type as our own row count — the row the engine took, since
	//: CU-869fh646w — unless the strategy compares against every kind.
	no_competitors_in_scope:
		'No competitor matched both this strategy\u2019s market scope and our '
		+ 'own row\u2019s insurance and tariff type, so there was nothing to '
		+ 'price against.',
	//: The row taken has no kind recorded on an axis that still filters
	//: (CU-869fh646w, plan § 1.2): like with like cannot be judged, so
	//: nothing was priced and the market was not read. The fix is in the
	//: strategy — switch that axis off — not in Setup.
	own_kind_unknown_insurance:
		'Our own row on this rate shop has no insurance recorded, so like with '
		+ 'like could not be judged \u2014 nothing was priced. Switch on '
		+ '\u201cCompare against every insurance\u201d in the strategy to compare anyway.',
	own_kind_unknown_tariff:
		'Our own row on this rate shop has no tariff type recorded, so like with '
		+ 'like could not be judged \u2014 nothing was priced. Switch on '
		+ '\u201cCompare against every tariff type\u201d in the strategy to compare anyway.',
	own_kind_unknown_both:
		'Our own row on this rate shop has neither a tariff type nor an insurance '
		+ 'recorded, so like with like could not be judged \u2014 nothing was priced. '
		+ 'Switch on both \u201cCompare against every \u2026\u201d switches in the '
		+ 'strategy to compare anyway.',
	//: A face with an axis unset used to price nothing (Curro, 2026-10-07).
	//: Retired by CU-869fh646w — blank means any — and the rate cards
	//: themselves by CU-869fhuhy6 (Products), but the rows written before
	//: are still read: historical sentences, pointing at no tab (UX v3 D12′).
	face_axis_missing_tariff:
		'This rate card had no tariff type set, so its competitors could not be '
		+ 'told apart \u2014 nothing was priced (rate card of the model before 16 Oct 2026).',
	face_axis_missing_insurance:
		'This rate card had no insurance set, so its competitors could not be '
		+ 'told apart \u2014 nothing was priced (rate card of the model before 16 Oct 2026).',
	face_axis_missing_both:
		'This rate card had neither a tariff type nor an insurance set, so its '
		+ 'competitors could not be told apart \u2014 nothing was priced (rate card of the model before 16 Oct 2026).',
	//: Emitted when no row of ours in the cell resolves any rate-card face.
	//: The type existed since the first engine but the screen never
	//: registered it, so it rendered as a raw grey chip (UX, 2026-10-07).
	//: Since the products (CU-869fhuhy6) the engine says the same of a
	//: product row whose kind no own row matches.
	'no rate card for this channel':
		'None of our offers in this cell matches a product row (tariff type, '
		+ 'insurance) on this site, so nothing was priced. Set the offer of the row in Setup \u2192 '
		+ 'Products to resume.',
	fewer_competitors_than_the_target_position:
		'Fewer competitors than the target position asks for — nobody holds the '
		+ 'slot the strategy aims at, so there is no price to work from.',
	//: The two codes the incomplete-market branch emits are deliberately
	//: absent. They need the figures of the decision to be worth reading,
	//: so the Reason block composes them from the payload through
	//: `@services/incompleteMarket` — a second copy here would be a
	//: second source of truth that nothing renders (QA, 2026-10-05).
}

/** The engine's `payload.reason` in words: a code it knows, or the engine's own sentence as it came; `null` without. */
export function reasonText(code) {
	return REASON_TEXT[code] || code || null
}

/**
 * The detail's Reason line for one decision, or `null` when the engine recorded none. `days` = the stay, for the
 * incomplete-market figures; `faceName` = the face's label for `no_strategy` (`null` → its "no longer exists" form).
 */
export function decisionReason(row, days, faceName = null) {
	if (!row) return null
	const im = incompleteMarketNoChange(row, days)
	// `canonicalEventType` only aliases `blocked_by_limit`, so `no_strategy` is compared as it comes
	const ns = row.event_type === 'no_strategy' ? noStrategyCopy(row, faceName) : null
	return im?.reason || ns?.reason || row.reason || null
}
